/**
 * CompanyDetail — corporate account detail (profile · customers · ledger ·
 * payments · monthly statement). Read-only over rides; the only writes are the
 * company profile, customer links, and recorded payments.
 */
import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  ArrowLeft, Plus, X, Receipt, FileText, Users, RefreshCw, Banknote, Download, Mail, CalendarClock,
  CreditCard, BadgeCheck, XCircle, Loader2,
} from "lucide-react";
import { useFinancialPrivacy } from "../../lib/financialPrivacy";

const gbp = (n) => {
  const v = Number(n ?? 0);
  return v < 0 ? `-£${Math.abs(v).toFixed(2)}` : `£${v.toFixed(2)}`;
};
// Receivable semantics: >0 they owe us (red), <0 in credit (blue), 0 settled (green).
const balText = (v) => (Number(v) < 0 ? `Credit £${Math.abs(Number(v)).toFixed(2)}` : gbp(v));
const balColor = (v) => (Number(v) > 0 ? "#EF4444" : Number(v) < 0 ? "#002FA7" : "#10B981");
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—");

const Field = ({ label, value, onChange, ...rest }) => (
  <label className="block">
    <span className="label-mini text-muted-foreground">{label}</span>
    <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="mt-1" {...rest} />
  </label>
);

export function CompanyDetail({ companyId, passengers, onBack, onChanged }) {
  const [company, setCompany] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [statement, setStatement] = useState(null);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [view, setView] = useState("ledger"); // ledger | statement
  const [edit, setEdit] = useState(null);
  const [linkId, setLinkId] = useState("");
  const [pay, setPay] = useState({ amount: "", kind: "payment", note: "" });
  const [loading, setLoading] = useState(true);
  const [emailing, setEmailing] = useState(false);
  const [retainer, setRetainer] = useState(null);
  const [retPlan, setRetPlan] = useState("");
  const [retBusy, setRetBusy] = useState(false);
  const { mask } = useFinancialPrivacy();

  const loadRetainer = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/companies/${companyId}/retainer`);
      setRetainer(data);
    } catch (e) {
      // non-fatal — retainer is an optional add-on
    }
  }, [companyId]);

  const load = useCallback(async () => {
    try {
      const [c, l] = await Promise.all([
        api.get(`/admin/companies/${companyId}`),
        api.get(`/admin/companies/${companyId}/ledger`),
      ]);
      setCompany(c.data);
      setEdit(c.data);
      setLedger(l.data);
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to load company");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); loadRetainer(); }, [load, loadRetainer]);

  // Pre-select the first active retainer tier so the "Start retainer" button is
  // immediately actionable (avoids a silent no-op when the operator clicks it
  // before opening the dropdown).
  useEffect(() => {
    const plans = retainer?.plans || [];
    if (plans.length > 0 && !retPlan) setRetPlan(plans[0].id);
  }, [retainer, retPlan]);

  const startRetainer = async () => {
    if (!retPlan) { toast.error("Choose a retainer tier first"); return; }
    setRetBusy(true);
    try {
      const { data } = await api.post(`/admin/companies/${companyId}/retainer/checkout`, {
        plan_id: retPlan,
        origin_url: window.location.origin,
      });
      if (data?.url) window.location.href = data.url;
      else toast.error("Couldn't start the retainer checkout");
    } catch (e) {
      const status = e?.response?.status;
      toast.error(
        status === 502 || typeof e?.response?.data !== "object"
          ? "Could not start the retainer checkout. Please try again later."
          : (e?.response?.data?.detail || formatApiError(e))
      );
    } finally {
      setRetBusy(false);
    }
  };

  const checkRetainerStatus = async () => {
    const sid = retainer?.subscription?.stripe_session_id;
    if (!sid) { loadRetainer(); return; }
    setRetBusy(true);
    try {
      const { data } = await api.get(`/admin/companies/${companyId}/retainer/status/${sid}`);
      setRetainer(data);
      toast.success("Retainer status refreshed");
    } catch (e) {
      toast.error(formatApiError(e) || "Couldn't refresh status");
    } finally {
      setRetBusy(false);
    }
  };

  const manageRetainerBilling = async () => {
    setRetBusy(true);
    try {
      const { data } = await api.post(`/admin/companies/${companyId}/retainer/billing-portal`, {
        return_url: window.location.href,
      });
      if (data?.url) window.location.href = data.url;
      else toast.error("Couldn't open the billing portal");
    } catch (e) {
      const status = e?.response?.status;
      toast.error(
        status === 502 || typeof e?.response?.data !== "object"
          ? "Could not open the billing portal. Please try again later."
          : (e?.response?.data?.detail || formatApiError(e))
      );
    } finally {
      setRetBusy(false);
    }
  };

  const cancelRetainer = async () => {
    if (!window.confirm("Cancel this company's retainer? It stays active until the end of the current billing period.")) return;
    setRetBusy(true);
    try {
      const { data } = await api.post(`/admin/companies/${companyId}/retainer/cancel`);
      setRetainer(data);
      toast.success("Retainer will end at the period close");
    } catch (e) {
      toast.error(formatApiError(e) || "Couldn't cancel the retainer");
    } finally {
      setRetBusy(false);
    }
  };

  const sendRetainerCheckoutLink = async () => {
    if (!retPlan) { toast.error("Choose a retainer tier first"); return; }
    setRetBusy(true);
    try {
      const { data } = await api.post(`/admin/companies/${companyId}/retainer/send-checkout-link`, {
        plan_id: retPlan,
        origin_url: window.location.origin,
      });
      const ok = data?.email_status === "sent";
      toast[ok ? "success" : "message"](
        ok ? `Checkout link emailed to ${data.sent_to}` : `Link created — email ${data?.email_status || "not sent"} (check the billing email / Resend config)`
      );
    } catch (e) {
      const status = e?.response?.status;
      toast.error(
        status === 502 || typeof e?.response?.data !== "object"
          ? "Could not create the checkout link. Please try again later."
          : (e?.response?.data?.detail || formatApiError(e))
      );
    } finally {
      setRetBusy(false);
    }
  };

  const loadStatement = useCallback(async (m) => {
    try {
      const { data } = await api.get(`/admin/companies/${companyId}/statement`, { params: { month: m } });
      setStatement(data);
    } catch (e) { toast.error(formatApiError(e) || "Failed to load statement"); }
  }, [companyId]);

  useEffect(() => { if (view === "statement") loadStatement(month); }, [view, month, loadStatement]);

  const saveProfile = async () => {
    try {
      await api.put(`/admin/companies/${companyId}`, {
        name: edit.name, contact_name: edit.contact_name, billing_email: edit.billing_email,
        phone: edit.phone, address: edit.address, status: edit.status,
        payment_terms_days: Number(edit.payment_terms_days) || 0,
        credit_limit: edit.credit_limit === "" || edit.credit_limit == null ? null : Number(edit.credit_limit),
        notes: edit.notes,
      });
      toast.success("Company updated");
      // Optimistic: reflect the status flip immediately, then reconcile via load().
      setCompany((prev) => (prev ? { ...prev, status: edit.status, is_active: edit.status === "active" } : prev));
      load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e) || "Update failed"); }
  };

  const downloadStatement = async (fmt) => {
    try {
      const res = await api.get(`/admin/companies/${companyId}/statement.${fmt}`, {
        params: { month }, responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `statement_${(company?.name || "company").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}_${month}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e) || `${fmt.toUpperCase()} export failed`); }
  };

  const emailStatement = async () => {
    if (!company?.billing_email) {
      toast.error("This company has no billing email. Add one in the profile above before emailing a statement.");
      return;
    }
    setEmailing(true);
    try {
      const { data } = await api.post(`/admin/companies/${companyId}/email-statement`, null, { params: { month } });
      toast.success(`Statement emailed to ${data.recipient}`);
      load(); onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to email statement");
    } finally {
      setEmailing(false);
    }
  };

  const linkCustomer = async () => {
    if (!linkId) return;
    try {
      await api.post(`/admin/companies/${companyId}/customers`, { customer_id: linkId });
      toast.success("Customer linked"); setLinkId(""); load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e) || "Link failed"); }
  };

  const unlinkCustomer = async (cid) => {
    try {
      await api.delete(`/admin/companies/${companyId}/customers/${cid}`);
      toast.success("Customer removed"); load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e) || "Remove failed"); }
  };

  const recordPayment = async () => {
    const amt = Number(pay.amount);
    if (!amt || amt <= 0) { toast.error("Enter a positive amount"); return; }
    try {
      await api.post(`/admin/companies/${companyId}/payments`, {
        amount: amt, kind: pay.kind, note: pay.note || null,
      });
      toast.success(`${pay.kind === "payment" ? "Payment" : "Adjustment"} recorded`);
      setPay({ amount: "", kind: "payment", note: "" });
      load(); if (view === "statement") loadStatement(month); onChanged?.();
    } catch (e) { toast.error(formatApiError(e) || "Failed to record"); }
  };

  if (loading || !company) {
    return <div className="p-8 text-center text-muted-foreground"><RefreshCw className="w-5 h-5 animate-spin mx-auto" /></div>;
  }

  const linkedIds = new Set((company.customer_list || []).map((c) => c.id));
  const available = (passengers || []).filter((p) => !linkedIds.has(p.id));

  return (
    <div className="space-y-5" data-testid="company-detail">
      <button onClick={onBack} className="inline-flex items-center gap-2 text-sm font-bold text-muted-foreground hover:text-foreground" data-testid="company-back">
        <ArrowLeft className="w-4 h-4" /> All companies
      </button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-black tracking-tight flex items-center gap-2" data-testid="company-name">
            {company.name}
            <span
              className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-sm ${company.status === "suspended" ? "bg-[#FEE2E2] text-[#991B1B]" : "bg-[#D1FAE5] text-[#065F46]"}`}
              data-testid="company-status-badge"
            >
              {company.status === "suspended" ? "Suspended" : "Active"}
            </span>
          </h2>
          <div className="text-xs text-muted-foreground">
            {company.customers} customer{company.customers === 1 ? "" : "s"} · {company.rides_total} account rides · {company.payment_terms_days}-day terms
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            This month <strong>{mask(gbp(company.mtd?.charges))}</strong> ({company.mtd?.rides} rides) · Last month <strong>{mask(gbp(company.prev_month?.charges))}</strong> ({company.prev_month?.rides} rides)
          </div>
        </div>
        <div className="text-right">
          <div className="label-mini text-muted-foreground">OUTSTANDING BALANCE</div>
          <div className="font-heading text-3xl font-black" style={{ color: balColor(company.balance) }} data-testid="company-balance">
            {mask(balText(company.balance))}
          </div>
        </div>
      </div>

      {/* Statement schedule strip (Phase 2) */}
      <div className="bg-white border border-border p-3 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm" data-testid="company-statement-schedule">
        <div>
          <div className="label-mini text-muted-foreground">LAST STATEMENT SENT</div>
          <div className="font-semibold mt-0.5" data-testid="company-last-sent">
            {company.last_statement_sent_at ? fmtDate(company.last_statement_sent_at) : "Never"}
          </div>
        </div>
        <div>
          <div className="label-mini text-muted-foreground">LAST PERIOD</div>
          <div className="font-semibold mt-0.5" data-testid="company-last-period">
            {company.last_statement_period_label || company.last_statement_period || "—"}
          </div>
        </div>
        <div>
          <div className="label-mini text-muted-foreground flex items-center gap-1"><CalendarClock className="w-3 h-3" /> NEXT SCHEDULED</div>
          <div className="font-semibold mt-0.5" data-testid="company-next-scheduled">{fmtDate(company.next_scheduled_statement)}</div>
        </div>
      </div>

      {/* Corporate Retainer (Subscriptions Phase 2) — a flat recurring fee for
          priority service. SEPARATE from and ADDITIVE to per-ride account
          charges; it does not affect the ledger above. */}
      {(() => {
        const sub = retainer?.subscription;
        const plans = retainer?.plans || [];
        const hasActive = sub && ["active", "past_due"].includes(sub.status);
        const RET_STATUS = {
          active: "bg-[#DCFCE7] text-[#166534] border-[#16A34A]",
          past_due: "bg-[#FEF3C7] text-[#92400E] border-[#F59E0B]",
          canceled: "bg-[#FEE2E2] text-[#991B1B] border-[#EF4444]",
          incomplete: "bg-secondary text-muted-foreground border-border",
        };
        return (
          <div className="bg-white border border-border p-4" data-testid="company-retainer">
            <div className="label-mini text-muted-foreground mb-2 flex items-center gap-2">
              <CreditCard className="w-3 h-3" /> CORPORATE RETAINER
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              A flat recurring retainer for priority service, account management & SLA. Billed separately from — and in addition to — the company's pay-per-ride account charges.
            </p>

            {hasActive ? (
              <div className="space-y-3" data-testid="company-retainer-active">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <BadgeCheck className="w-5 h-5 text-[#16A34A]" />
                    <span className="font-heading text-lg font-black">{sub.plan_name}</span>
                  </div>
                  <span className={`text-xs font-bold px-2 py-1 border ${RET_STATUS[sub.status] || RET_STATUS.incomplete}`} data-testid="company-retainer-status">
                    {sub.status}{sub.cancel_at_period_end ? " (ending)" : ""}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-px bg-border border border-border">
                  <div className="bg-white p-3">
                    <div className="label-mini text-muted-foreground">PLAN</div>
                    <div className="font-semibold mt-0.5">{gbp(sub.amount_gbp)}/{sub.interval}</div>
                  </div>
                  <div className="bg-white p-3">
                    <div className="label-mini text-muted-foreground flex items-center gap-1"><CalendarClock className="w-3 h-3" /> NEXT BILLING</div>
                    <div className="font-semibold mt-0.5" data-testid="company-retainer-next-billing">{fmtDate(sub.current_period_end)}</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button onClick={manageRetainerBilling} disabled={retBusy} variant="outline" size="sm" data-testid="company-retainer-portal-btn">
                    {retBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CreditCard className="w-4 h-4 mr-1.5" /> Manage billing</>}
                  </Button>
                  {!sub.cancel_at_period_end && (
                    <Button onClick={cancelRetainer} disabled={retBusy} variant="outline" size="sm" className="border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444] hover:text-white" data-testid="company-retainer-cancel-btn">
                      <XCircle className="w-4 h-4 mr-1.5" /> Cancel retainer
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap items-end gap-2" data-testid="company-retainer-setup">
                {plans.length === 0 ? (
                  <span className="text-sm text-muted-foreground italic" data-testid="company-retainer-no-plans">
                    No active retainer tiers. Activate tiers in the Subscriptions tab first.
                  </span>
                ) : (
                  <>
                    <label className="block">
                      <span className="label-mini text-muted-foreground">RETAINER TIER</span>
                      <select value={retPlan} onChange={(e) => setRetPlan(e.target.value)} className="block mt-1 border border-border px-2 py-2 text-sm h-10 min-w-[200px]" data-testid="company-retainer-plan-select">
                        <option value="">Choose a tier…</option>
                        {plans.map((p) => <option key={p.id} value={p.id}>{p.name} · {gbp(p.amount_gbp)}/{p.interval}</option>)}
                      </select>
                    </label>
                    <Button onClick={startRetainer} disabled={retBusy || !retPlan} className="bg-[#0A0A0A] text-white h-10" data-testid="company-retainer-start-btn">
                      {retBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CreditCard className="w-4 h-4 mr-1.5" /> Start retainer</>}
                    </Button>
                    <Button onClick={sendRetainerCheckoutLink} disabled={retBusy || !retPlan} variant="outline" className="h-10" data-testid="company-retainer-send-link-btn" title="Email the Stripe checkout link to the company's billing contact">
                      {retBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-4 h-4 mr-1.5" /> Email checkout link</>}
                    </Button>
                    {sub && sub.status === "incomplete" && (
                      <Button onClick={checkRetainerStatus} disabled={retBusy} variant="outline" size="sm" className="h-10" data-testid="company-retainer-check-status">
                        <RefreshCw className="w-4 h-4 mr-1.5" /> Check payment status
                      </Button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* Profile editor */}
      <div className="bg-white border border-border p-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="company-profile">
        <Field label="COMPANY NAME" value={edit.name} onChange={(v) => setEdit({ ...edit, name: v })} data-testid="company-edit-name" />
        <Field label="CONTACT NAME" value={edit.contact_name} onChange={(v) => setEdit({ ...edit, contact_name: v })} />
        <Field label="BILLING EMAIL" value={edit.billing_email} onChange={(v) => setEdit({ ...edit, billing_email: v })} />
        <Field label="PHONE" value={edit.phone} onChange={(v) => setEdit({ ...edit, phone: v })} />
        <Field label="ADDRESS" value={edit.address} onChange={(v) => setEdit({ ...edit, address: v })} />
        <Field label="PAYMENT TERMS (DAYS)" type="number" value={edit.payment_terms_days} onChange={(v) => setEdit({ ...edit, payment_terms_days: v })} />
        <Field label="CREDIT LIMIT (£, optional)" type="number" value={edit.credit_limit} onChange={(v) => setEdit({ ...edit, credit_limit: v })} />
        <label className="block">
          <span className="label-mini text-muted-foreground">ACCOUNT STATUS</span>
          <select value={edit.status || "active"} onChange={(e) => setEdit({ ...edit, status: e.target.value })} className="mt-1 w-full border border-border px-2 py-2 text-sm h-10" data-testid="company-status-select">
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
          </select>
        </label>
        <Field label="NOTES" value={edit.notes} onChange={(v) => setEdit({ ...edit, notes: v })} />
        <div className="md:col-span-2">
          <Button onClick={saveProfile} className="bg-[#0A0A0A] text-white" data-testid="company-save-profile">Save profile</Button>
        </div>
      </div>

      {/* Customers */}
      <div className="bg-white border border-border p-4" data-testid="company-customers">
        <div className="label-mini text-muted-foreground mb-2 flex items-center gap-2"><Users className="w-3 h-3" /> LINKED CUSTOMERS</div>
        <div className="flex flex-wrap gap-2 mb-3">
          {(company.customer_list || []).length === 0 && <span className="text-sm text-muted-foreground italic">No customers linked yet.</span>}
          {(company.customer_list || []).map((c) => (
            <span key={c.id} className="inline-flex items-center gap-2 bg-secondary px-2 py-1 text-xs" data-testid={`company-customer-${c.id}`}>
              {c.name || c.email}
              <button onClick={() => unlinkCustomer(c.id)} className="text-muted-foreground hover:text-[#EF4444]" data-testid={`company-unlink-${c.id}`}><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <select value={linkId} onChange={(e) => setLinkId(e.target.value)} className="border border-border px-2 py-1.5 text-sm flex-1 max-w-xs" data-testid="company-link-select">
            <option value="">Add a customer…</option>
            {available.map((p) => <option key={p.id} value={p.id}>{p.name || p.email}</option>)}
          </select>
          <Button onClick={linkCustomer} disabled={!linkId} variant="outline" data-testid="company-link-btn"><Plus className="w-3 h-3 mr-1" /> Link</Button>
        </div>
      </div>

      {/* Record payment */}
      <div className="bg-white border border-border p-4" data-testid="company-payment-form">
        <div className="label-mini text-muted-foreground mb-2 flex items-center gap-2"><Banknote className="w-3 h-3" /> RECORD PAYMENT / ADJUSTMENT</div>
        <div className="flex flex-wrap items-end gap-2">
          <label className="block">
            <span className="label-mini text-muted-foreground">AMOUNT (£)</span>
            <Input type="number" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} className="mt-1 w-32" data-testid="company-payment-amount" />
          </label>
          <select value={pay.kind} onChange={(e) => setPay({ ...pay, kind: e.target.value })} className="border border-border px-2 py-2 text-sm h-10" data-testid="company-payment-kind">
            <option value="payment">Payment received</option>
            <option value="adjustment">Adjustment / credit</option>
          </select>
          <Input value={pay.note} onChange={(e) => setPay({ ...pay, note: e.target.value })} placeholder="Note (e.g. BACS ref)" className="flex-1 min-w-[160px]" data-testid="company-payment-note" />
          <Button onClick={recordPayment} className="bg-[#10B981] text-white h-10" data-testid="company-payment-submit">Record</Button>
        </div>
      </div>

      {/* Ledger / Statement toggle */}
      <div className="flex items-center gap-2">
        <Button variant={view === "ledger" ? "default" : "outline"} size="sm" onClick={() => setView("ledger")} data-testid="company-view-ledger"><Receipt className="w-3 h-3 mr-1" /> Ledger</Button>
        <Button variant={view === "statement" ? "default" : "outline"} size="sm" onClick={() => setView("statement")} data-testid="company-view-statement"><FileText className="w-3 h-3 mr-1" /> Monthly statement</Button>
        {view === "statement" && (
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="border border-border px-2 py-1.5 text-sm ml-auto" data-testid="company-statement-month" />
        )}
      </div>

      {view === "ledger" ? (
        <div className="bg-white border border-border overflow-x-auto" data-testid="company-ledger">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border bg-secondary">
              <th className="px-3 py-2 text-left label-mini text-muted-foreground">Date</th>
              <th className="px-3 py-2 text-left label-mini text-muted-foreground">Detail</th>
              <th className="px-3 py-2 text-right label-mini text-muted-foreground">Charge</th>
              <th className="px-3 py-2 text-right label-mini text-muted-foreground">Credit</th>
              <th className="px-3 py-2 text-right label-mini text-muted-foreground">Balance</th>
            </tr></thead>
            <tbody>
              {(ledger?.entries || []).length === 0 ? (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground italic" data-testid="company-ledger-empty">No activity yet.</td></tr>
              ) : ledger.entries.map((e, i) => (
                <tr key={i} className="border-b border-border last:border-0" data-testid="company-ledger-row">
                  <td className="px-3 py-2 font-mono-tab whitespace-nowrap">{fmtDate(e.ts)}</td>
                  <td className="px-3 py-2">
                    {e.type === "charge" ? (
                      <span><span className="text-muted-foreground">{e.customer}</span> · {e.description || "Account ride"}</span>
                    ) : (
                      <span className="uppercase text-xs font-bold">{e.type}{e.note ? <span className="font-normal text-muted-foreground"> · {e.note}</span> : null}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-mono-tab">{e.type === "charge" ? mask(gbp(e.amount)) : ""}</td>
                  <td className="px-3 py-2 text-right font-mono-tab text-[#10B981]">{e.type !== "charge" ? mask(gbp(e.amount)) : ""}</td>
                  <td className="px-3 py-2 text-right font-mono-tab font-bold">{mask(gbp(e.running_balance))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        statement && (
          <div className="bg-white border border-border p-5 space-y-4" data-testid="company-statement">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="font-heading text-xl font-black">{statement.month_label}</h3>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{statement.rides_count} rides</span>
                <Button size="sm" variant="outline" onClick={() => downloadStatement("csv")} data-testid="statement-export-csv"><Download className="w-3 h-3 mr-1" /> CSV</Button>
                <Button size="sm" variant="outline" onClick={() => downloadStatement("pdf")} data-testid="statement-export-pdf"><Download className="w-3 h-3 mr-1" /> PDF</Button>
                <Button size="sm" onClick={emailStatement} disabled={emailing || !company.billing_email} title={!company.billing_email ? "Add a billing email in the profile first" : "Email this statement to the billing contact"} className="bg-[#002FA7] text-white" data-testid="statement-email-btn">
                  <Mail className="w-3 h-3 mr-1" /> {emailing ? "Sending…" : "Email statement"}
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border">
              <div className="bg-white p-3"><div className="label-mini text-muted-foreground">OPENING</div><div className="font-bold text-lg" data-testid="statement-opening">{mask(gbp(statement.opening_balance))}</div></div>
              <div className="bg-white p-3"><div className="label-mini text-muted-foreground">CHARGES</div><div className="font-bold text-lg" data-testid="statement-charges">{mask(gbp(statement.period_charges))}</div></div>
              <div className="bg-white p-3"><div className="label-mini text-muted-foreground">PAYMENTS</div><div className="font-bold text-lg text-[#10B981]" data-testid="statement-payments">{mask(gbp(statement.period_payments))}</div></div>
              <div className="bg-white p-3"><div className="label-mini text-muted-foreground">CLOSING</div><div className="font-bold text-lg" style={{ color: balColor(statement.closing_balance) }} data-testid="statement-closing">{mask(balText(statement.closing_balance))}</div></div>
            </div>
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border bg-secondary">
                <th className="px-3 py-2 text-left label-mini text-muted-foreground">Date</th>
                <th className="px-3 py-2 text-left label-mini text-muted-foreground">Customer</th>
                <th className="px-3 py-2 text-left label-mini text-muted-foreground">Journey</th>
                <th className="px-3 py-2 text-right label-mini text-muted-foreground">Fare</th>
              </tr></thead>
              <tbody>
                {statement.line_items.length === 0 ? (
                  <tr><td colSpan={4} className="px-3 py-6 text-center text-muted-foreground italic" data-testid="statement-empty">No account rides this month.</td></tr>
                ) : statement.line_items.map((li) => (
                  <tr key={li.ride_id} className="border-b border-border last:border-0">
                    <td className="px-3 py-2 font-mono-tab whitespace-nowrap">{fmtDate(li.date)}</td>
                    <td className="px-3 py-2">{li.customer}</td>
                    <td className="px-3 py-2 text-xs">{li.description}</td>
                    <td className="px-3 py-2 text-right font-mono-tab">{mask(gbp(li.amount))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}

export default CompanyDetail;
